import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": `${import.meta.env.VITE_SITE_URL}/#business`,
      "name": "DVS Power Washing",
      "url": import.meta.env.VITE_SITE_URL,
      "telephone": "+12146368343",
      "description": "Professional pressure washing services in Dallas Metroplex, Texas. House washing, driveway cleaning, roof soft washing, fence & patio cleaning, and commercial pressure washing. Family minority owned. 5.0 star rated.",
      "priceRange": "$$",
      "image": `${import.meta.env.VITE_SITE_URL}/og-image.jpg`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Dallas",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 32.7767,
        "longitude": -96.7970
      },
      "areaServed": [
        "Dallas", "Fort Worth", "Plano", "Irving", "Frisco",
        "McKinney", "Arlington", "Garland", "Richardson",
        "Grand Prairie", "Carrollton", "Denton"
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "07:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "08:00",
          "closes": "16:00"
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "50",
        "bestRating": "5",
        "worstRating": "1"
      },
      "sameAs": [
        "https://www.instagram.com/dvs_power_washing_dallas/"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Pressure Washing Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "House Washing" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Driveway & Sidewalk Cleaning" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Soft Washing" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Fence & Patio Cleaning" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Commercial Pressure Washing" } }
        ]
      }
    },
    {
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL}/#website`,
      "url": import.meta.env.VITE_SITE_URL,
      "name": "DVS Power Washing",
      "description": "Professional pressure washing services in Dallas Metroplex, Texas.",
      "publisher": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` }
    }
  ]
};

const HomePage = () => {
  useEffect(() => {
    // Canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://dvspowerwashingdallas.com/';

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.text = JSON.stringify(HOME_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  const whyChooseUs = [
    {
      icon: 'ri-team-fill',
      title: 'Family Minority Owned',
    },
    {
      icon: 'ri-award-fill',
      title: 'Quality Over Corners',
    },
    {
      icon: 'ri-customer-service-2-fill',
      title: '100% Customer Satisfaction',
    },
    {
      icon: 'ri-time-fill',
      title: 'Reliable & On-Time Service',
    },
    {
      icon: 'ri-hand-coin-fill',
      title: 'Fair, Honest Pricing',
    },
  ];

  const services = [
    {
      icon: 'ri-home-4-fill',
      title: 'House Washing',
      description: 'Safe exterior cleaning to remove dirt and mold',
    },
    {
      icon: 'ri-road-map-fill',
      title: 'Driveways & Sidewalks',
      description: 'Restore curb appeal and eliminate stains',
    },
    {
      icon: 'ri-building-fill',
      title: 'Roof Soft Washing',
      description: 'Low-pressure system to safely clean roofs',
    },
    {
      icon: 'ri-layout-grid-fill',
      title: 'Fence & Patio Cleaning',
      description: 'Bring outdoor spaces back to life',
    },
    {
      icon: 'ri-store-2-fill',
      title: 'Commercial Pressure Washing',
      description: 'Professional cleaning for businesses',
    },
    {
      icon: 'ri-tools-fill',
      title: 'Custom Solutions',
      description: 'Tailored cleaning for your needs',
    },
  ];

  const reviews = [
    {
      text: 'My sidewalk looks like new I took a picture when he was halfway done and you can see the huge amount of dirt that was removed. What a professional company.',
      author: 'Sara. B',
    },
    {
      text: 'Best pressure washing company in Dallas. Fair pricing and exceptional results. Will use again!',
      author: 'Sarah T.',
    },
    {
      text: 'Incredible results on my driveway and patio. They were on time, professional, and the quality of work was outstanding. Highly recommend DVS!',
      author: 'Marcus R.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative h-screen min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a3c3631ea9dca7d933c6b9dc0296ef85.jpg"
            alt="Professional Pressure Washing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="mb-6">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-2 leading-tight">
              DVS Power Washing
            </h1>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="bg-white text-[#0E3A5D] px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                <i className="ri-star-fill text-yellow-500"></i>
                <span>5.0 Rated</span>
              </span>
            </div>
          </div>
          
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Professional Pressure Washing in Dallas Metroplex, Texas
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            High-quality exterior cleaning done right the first time. Satisfaction guaranteed.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <a
              href="tel:2146368343"
              className="bg-[#4FB3D9] text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-[#3A9DC4] transition-all whitespace-nowrap flex items-center space-x-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>Call (214) 636-8343</span>
            </a>
            <Link
              to="/contact"
              className="bg-white text-[#0E3A5D] px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all whitespace-nowrap w-full sm:w-auto text-center"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-white/95 py-6">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center">
              <div className="flex items-center space-x-2">
                <i className="ri-team-fill text-[#0E3A5D] text-2xl"></i>
                <span className="text-sm font-semibold text-gray-800">Family Minority Owned</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <i className="ri-star-fill text-[#FFB800] text-2xl"></i>
                <span className="text-sm font-semibold text-gray-800">5.0 Star Rated</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-fill text-[#0E3A5D] text-2xl"></i>
                <span className="text-sm font-semibold text-gray-800">Licensed &amp; Insured</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0E3A5D] text-center mb-16">
            Why Choose Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5">
                  <i className={`${item.icon} text-5xl text-[#0E3A5D]`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="relative">
                <i className="ri-double-quotes-l text-9xl text-[#4FB3D9] opacity-20 absolute -top-8 -left-4"></i>
                <p className="text-xs font-bold text-[#0E3A5D] uppercase tracking-wider mb-6">About Us</p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
                Committed to Quality and Customer Satisfaction
              </h2>
              <p className="text-lg text-[#5A6C7D] leading-relaxed mb-8">
                At DVS Power Washing, we are a proud family minority owned business dedicated to excellence in every job. We don't cut corners—we clean them. Your satisfaction is our mission, and we bring precision and care to every project we undertake.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 text-[#0E3A5D] font-semibold hover:text-[#4FB3D9] transition-colors"
              >
                <span>Learn Our Story</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0E3A5D] text-center mb-20">
            Our Services
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer"
              >
                <div className="w-18 h-18 flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-7xl text-[#0E3A5D]`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-base text-[#5A6C7D] leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-block bg-white border-2 border-[#0E3A5D] text-[#0E3A5D] px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#0E3A5D] hover:text-white transition-all whitespace-nowrap"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-7xl font-bold text-[#0E3A5D] mb-3">5.0</div>
            <div className="flex items-center justify-center space-x-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-[#FFB800] text-3xl"></i>
              ))}
            </div>
            <p className="text-base text-[#5A6C7D]">Based on 50+ Reviews</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FFB800] text-lg"></i>
                  ))}
                </div>
                <p className="text-base text-[#5A6C7D] italic mb-4 leading-relaxed">
                  "{review.text}"
                </p>
                <p className="text-sm font-bold text-gray-800">— {review.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0E3A5D] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1200 200" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,100 Q300,50 600,100 T1200,100 L1200,200 L0,200 Z" fill="white" />
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-5">
            Proudly Serving Greater Dallas Metroplex
          </h2>
          <p className="text-lg lg:text-xl text-white/90">
            Dallas | Fort Worth | Plano | Irving | Frisco | McKinney | Arlington | Surrounding Areas
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0E3A5D] mb-6">
            Ready to Restore Your Property?
          </h2>
          <p className="text-xl text-[#5A6C7D] mb-10">
            Get your free estimate today — no obligation
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
            <a
              href="tel:2146368343"
              className="bg-[#4FB3D9] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#3A9DC4] transition-all whitespace-nowrap flex items-center space-x-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>Call (214) 636-8343</span>
            </a>
            <Link
              to="/contact"
              className="bg-[#0E3A5D] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#0A2D47] transition-all whitespace-nowrap w-full sm:w-auto text-center"
            >
              Request Free Quote
            </Link>
          </div>

          <div className="flex items-center justify-center">
            <a
              href="https://www.instagram.com/dvs_power_washing_dallas/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 bg-gradient-to-r from-[#f09433] via-[#e6683c] via-[#dc2743] via-[#cc2366] to-[#bc1888] text-white px-8 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-instagram-fill text-2xl"></i>
              </div>
              <span>Follow Us on Instagram</span>
              <span className="text-white/80 text-base font-normal">@dvs_power_washing_dallas</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
